import React from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { Field, reduxForm, Form } from 'redux-form';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { withRouter } from 'react-router-dom';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
  getProductPricePreview,
  resetProductPreview
} from 'bento-ordering/menus/actions/productPreview';
import { getIsCartTotalExceedMaximum } from 'bento-ordering/cart/actions/orderAmount';
import {
  ORDER_MADE_FOR_LENGTH,
  SPECIAL_INSTRUCTIONS_LENGTH
} from 'constants/menu/inputSize';

import MenuItemImage from 'components/Menu/MenuItemImage';
import MenuItemDescription from 'components/Menu/MenuItemDescription';
import MultiLineTextAreaField from 'components/MultiLineTextAreaField';
import TextInputField from 'components/TextInputField';
import QuantityInputField from 'bento-ordering/menus/components/QuantityInputField';
import MenuItemDetailPrice from 'components/Menu/MenuItemDetailPrice';
import VariantGroupForm from 'bento-ordering/menus/components/VariantGroupForm';
import { getIsEnabledAddToCartSelector } from 'bento-ordering/menus/selectors';
import { resetError, removeCartItem } from 'bento-ordering/cart/actions/';
import MenuItemSubmitContainer from 'bento-ordering/menus/containers/MenuItemSubmitContainer';
import { MENU_ITEM, CART_ITEM } from 'constants/menu/formTypes';
import StickyPaper from 'components/StickyPaper';
import { MENU_ITEM_PROPTYPES } from 'bento-ordering/base/constants/propTypes';
import {
  getCheckboxOrRadio,
  RADIO_BUTTON,
  buildFormModificationsForPricePreview
} from 'helpers/forms/menuItem';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import StyledMenuDetailsModalBoxWrapper from 'components/StyledMenuDetailsModalBoxWrapper';
import ItemDetailModalPreviewMenuMessage from 'bento-ordering/menus/components/ItemDetailModalPreviewMenuMessage';
import { PRODUCT_VARIANT_FIELD_NAME } from 'constants/menu/menuItemDetailForm.js';
import { SPENDING_LIMIT_REACHED } from 'bento-ordering/group_ordering/constants/groupOrder';
import {
  selectCartIsGroupOrder,
  selectCartGroupOrderAdminName,
  selectCartGroupOrderCartClosed,
  selectIsGroupOrderCurrentDinerDoneOrdering,
  selectIsGroupOrderCurrentDinerAdmin
} from 'bento-ordering/cart/selectors/index';
import { closeMenuItemAndGoToMenu } from 'bento-ordering/menus/actions/menuItem';
import GroupOrderOrderConfirmedSnackbar from 'bento-ordering/group_ordering/components/GroupOrderOrderConfirmedSnackbar';
import { accountHasInvestorDayFeatureFlag } from 'bento-ordering/utils/featureFlags/hasInvestorDayFeatureFlag';

// util to compare previous and next props
// param prev obj
// param updated obj
// returns string of key name that has changed
// returns empty string if no change has occurred
// only one key will be returned
// only useful where values are flat primitives (integers, strings)
// could use lodash deepEqual if properties point to objects
const whichFieldChanged = (prev, updated) =>
  Object.keys(updated).reduce((acc, currKey) => {
    const updatedValue = updated[currKey];
    const prevValue = prev[currKey];
    if (updatedValue !== prevValue) {
      return currKey;
    }
    return acc;
  }, '');

const StyledBoxBorder = styled(Box)(({ theme }) => ({
  borderColor: theme.palette.menuItemNames.main
}));

class MenuItemDetailFormWrapper extends React.Component {
  static propTypes = {
    account: PropTypes.object,
    handleSubmit: PropTypes.func,
    itemForm: PropTypes.object,
    dispatch: PropTypes.func,
    defaultCtaName: PropTypes.string,
    preSelectedFields: PropTypes.object,
    initialize: PropTypes.func,
    isMaximumExceeded: PropTypes.bool,
    cartItemId: PropTypes.string,
    orderMaximum: PropTypes.string,
    productId: PropTypes.number,
    productName: PropTypes.string,
    pricePreview: PropTypes.string,
    inventoryIsDepletedWithCurrentQuantity: PropTypes.bool,
    previewedQuantityExceedsInventory: PropTypes.bool,
    inventory: PropTypes.number,
    inventoryLessCart: PropTypes.number,
    cartErrors: PropTypes.arrayOf(PropTypes.string),
    qtyCurrentlyInCart: PropTypes.number,
    isUpdatingPreview: PropTypes.bool,
    formType: PropTypes.oneOf([MENU_ITEM, CART_ITEM]),
    requestedQty: PropTypes.number,
    showOrderMadeForField: PropTypes.bool,
    showItemSpecialInstructionsField: PropTypes.bool.isRequired,
    menuItemImage: PropTypes.object,
    menuItemDescriptionProps: PropTypes.shape(MENU_ITEM_PROPTYPES),
    SkipToCheckoutButton: PropTypes.element,
    modifierGroups: PropTypes.array,
    variants: PropTypes.array.isRequired,
    spendingLimit: PropTypes.string,
    hasExceededSpendingLimit: PropTypes.bool,
    isGroupOrder: PropTypes.bool,
    isCartClosed: PropTypes.bool,
    isDinerDoneOrdering: PropTypes.bool,
    isCurrentDinerAdmin: PropTypes.bool,
    hasInvestorDayFeature: PropTypes.bool
  };

  static defaultProps = {
    cartErrors: [],
    formType: MENU_ITEM,
    SkipToCheckoutButton: null,
    modifierGroups: [],
    spendingLimit: '',
    hasExceededSpendingLimit: false,
    isGroupOrder: false
  };

  componentDidMount() {
    const {
      preSelectedFields,
      initialize,
      itemForm,
      modifierGroups
    } = this.props;
    if (preSelectedFields && !isEmpty(preSelectedFields)) {
      initialize(preSelectedFields);
    }

    /**
     * This is used to handle the case when a user clicks on a menu item instead
     * of loading the menu item detail link directly.
     */
    const currentValues = get(itemForm, 'menuItemDetail.values', {});
    const modifierGroupsExist = modifierGroups && modifierGroups.length > 0;
    if (modifierGroupsExist && isEmpty(currentValues)) {
      this.setDefaultSelection();
    }

    this.debouncedCalculatePricePreview();
  }

  componentWillUnmount() {
    this.props.dispatch(resetError());
    this.props.dispatch(resetProductPreview());
  }

  /**
   * Set the initial cost of the item and any additional cost update
   */
  componentDidUpdate(prevProps) {
    const {
      preSelectedFields,
      initialize,
      dispatch,
      cartItemId,
      productId,
      modifierGroups,
      itemForm
    } = this.props;

    /**
     * Attempt to set a initial default selection if variant fields exist for menu item
     *
     * This code is required in both componentDidMount AND componentDidUpdate because
     * when we load a menuitemdetail url directly, prevProps does not get populated
     * so the condition: prevProp.modifierGroups not exist && this.props.modifierGroups exist is
     * set to true which allows us to set the default option.
     * This is not the case for when we are on the menu page and we click on a menu item.
     * In this case, prevProps.modifierGroups exists so the logic in componentDidUpdate does not run.
     */
    const currentValues = get(itemForm, 'menuItemDetail.values', {});
    /* We need to check run the check for both prevProps.modifierGroups and this.props.modifierGroups
    to prevent an infinite loop from happening. */
    const previousModifierGroupsDoNotExist =
      prevProps.modifierGroups && prevProps.modifierGroups.length === 0;
    const currentModifierGroupsExist =
      modifierGroups && modifierGroups.length > 0;
    if (
      previousModifierGroupsDoNotExist &&
      currentModifierGroupsExist &&
      isEmpty(currentValues)
    ) {
      this.setDefaultSelection();
    }

    /**
     * calculate whether we exceed maximum whenever the price updates
     */
    if (prevProps.pricePreview !== this.props.pricePreview) {
      dispatch(getIsCartTotalExceedMaximum(cartItemId));
    }

    /* initial calculation for price preview once menu item is available */
    if (prevProps.productId !== productId) {
      this.debouncedCalculatePricePreview();
      dispatch(resetError());
    }

    /**
     * Prepopulate the menu item form when user wants to edit cart item
     */
    if (
      isEmpty(prevProps.preSelectedFields) &&
      prevProps.preSelectedFields !== preSelectedFields
    ) {
      initialize(preSelectedFields);
    }

    /* check to see if cta should be clickable. We only calculate the price if
    user make updates to fields other than text inputs */

    if (this.shouldRecalculatePrice(prevProps)) {
      this.debouncedCalculatePricePreview();
      dispatch(resetError());
    }
  }

  /**
   * If there are no preselected fields already
   * and the variant field only contains one option,
   * the variant field option is automatically selected.
   */
  setDefaultSelection() {
    const { preSelectedFields, initialize, modifierGroups } = this.props;
    if (!preSelectedFields && modifierGroups) {
      const defaultSelections = this.buildDefaultSelection();
      if (defaultSelections) {
        initialize(defaultSelections);
      }
    }
  }

  buildDefaultSelection() {
    const { modifierGroups } = this.props;

    const defaultSelections = {};
    modifierGroups.forEach(modifierGroup => {
      const enabledOptions = modifierGroup.options.filter(option => {
        return !option.disabled;
      });

      let multipleOptionsFound =
        modifierGroup.options.length !== modifierGroup.min_qty &&
        enabledOptions.length > 1;

      // force quantity field to behave like a checkbox
      if (
        modifierGroup.field_type === 'quantity' &&
        modifierGroup.min_qty === 1 &&
        modifierGroup.max_qty === 1
      ) {
        multipleOptionsFound = true;
      }

      if (multipleOptionsFound) {
        return;
      }
      // Determine if we only have one option total or only one enabled option
      const onlyHasOneEnabledOptionToSelect = enabledOptions.length === 1;
      const selectedOpt = onlyHasOneEnabledOptionToSelect
        ? enabledOptions[0]
        : modifierGroup.options[0];
      const isRadioButton =
        getCheckboxOrRadio(modifierGroup.min_qty, modifierGroup.max_qty) ===
        RADIO_BUTTON;
      const selectionkey = modifierGroup.slug;

      if (isRadioButton) {
        const radioButtonValue = selectedOpt.slug;
        defaultSelections[selectionkey] = radioButtonValue;
      } else {
        if (onlyHasOneEnabledOptionToSelect && modifierGroup.min_qty === 1) {
          const checkboxSelection = {};
          enabledOptions.forEach(option => {
            checkboxSelection[option.slug] = true;
          });
          defaultSelections[selectionkey] = checkboxSelection;
        }
      }
    });

    return defaultSelections;
  }

  shouldRecalculatePrice(prevProps) {
    const { itemForm, productId } = this.props;
    const currentValues = get(itemForm, 'menuItemDetail.values', {});
    const previousValues = get(prevProps, 'itemForm.menuItemDetail.values', {});

    // if the previous payload was empty, then we need to recalculate
    // if the current one isn't empty
    if (isEmpty(previousValues) && !isEmpty(currentValues)) {
      return true;
    }

    const nameOfFieldChanged = whichFieldChanged(previousValues, currentValues);

    const shouldRecalculatePrice =
      productId &&
      nameOfFieldChanged &&
      !['order-made-for', 'specialInstructions'].includes(nameOfFieldChanged);

    return shouldRecalculatePrice;
  }

  /**
   * Calculate the cost of a menu item based on the variants selected
   * and the total quantity.
   */
  calculatePricePreview = () => {
    const { dispatch, itemForm, productId, modifierGroups } = this.props;

    const values = get(itemForm, 'menuItemDetail.values');
    let payload = {};

    if (values) {
      const {
        quantityInput,
        [PRODUCT_VARIANT_FIELD_NAME]: variantId,
        ...modifications
      } = values;

      payload = {
        product_id: productId,
        quantity: quantityInput,
        required_fields: {
          ...buildFormModificationsForPricePreview(
            modifierGroups,
            modifications
          )
        }
      };

      if (variantId) {
        payload.variant_id = parseInt(variantId);
      }
    }
    if (productId) {
      dispatch(getProductPricePreview(productId, payload));
    }
  };

  debouncedCalculatePricePreview = debounce(this.calculatePricePreview, 500);

  removeItemFromCart = () => {
    const { dispatch, cartItemId } = this.props;

    dispatch(closeMenuItemAndGoToMenu());
    if (cartItemId) {
      dispatch(removeCartItem(parseInt(cartItemId)));
    }
  };

  render() {
    const {
      modifierGroups,
      itemForm,
      defaultCtaName,
      isMaximumExceeded,
      orderMaximum,
      // curr inventory for an item (doesn't take into account cart items)
      inventory,
      inventoryIsDepletedWithCurrentQuantity,
      previewedQuantityExceedsInventory,
      pricePreview,
      productName,
      cartErrors,
      qtyCurrentlyInCart,
      // excludes requested quantity from preview
      inventoryLessCart,
      isUpdatingPreview,
      formType,
      requestedQty = 0,
      showOrderMadeForField,
      showItemSpecialInstructionsField,
      menuItemImage,
      menuItemDescriptionProps,
      SkipToCheckoutButton,
      variants,
      spendingLimit,
      hasExceededSpendingLimit,
      isGroupOrder,
      isCartClosed,
      isDinerDoneOrdering,
      isCurrentDinerAdmin,
      hasInvestorDayFeature,
      preSelectedFields
    } = this.props;

    const hasCartError = !!cartErrors.length;
    const lowInventoryMessageQuantity =
      formType === CART_ITEM
        ? inventoryLessCart + preSelectedFields.quantityInput - requestedQty
        : inventoryLessCart - requestedQty;

    const shouldDisplayInventoryLeft =
      !!inventoryLessCart &&
      inventoryLessCart > 0 &&
      inventoryLessCart < 5 &&
      !previewedQuantityExceedsInventory &&
      lowInventoryMessageQuantity > 0;

    const shouldDisplayAddableInventory =
      !!inventoryLessCart &&
      inventoryLessCart > 0 &&
      !hasCartError &&
      (inventoryIsDepletedWithCurrentQuantity ||
        previewedQuantityExceedsInventory);

    const showNotesSection =
      (showOrderMadeForField && !isGroupOrder) ||
      showItemSpecialInstructionsField;

    return (
      <Form onSubmit={this.props.handleSubmit}>
        <StyledMenuDetailsModalBoxWrapper>
          {menuItemImage?.url && (
            <MenuItemImage
              url={menuItemImage.url}
              focalPointX={menuItemImage.focal_point_x}
              focalPointY={menuItemImage.focal_point_y}
            />
          )}
          <Box>
            <MenuItemDescription {...menuItemDescriptionProps} />
          </Box>
          <Box pl={2.5} pr={2.5}>
            <VariantGroupForm
              modifierGroups={modifierGroups}
              form={itemForm}
              variants={variants}
            />

            {showNotesSection && (
              <StyledBoxBorder
                width="100%"
                marginBottom={!showItemSpecialInstructionsField ? 2.5 : 0}
              >
                {showOrderMadeForField && !isGroupOrder && (
                  <Field
                    name="order-made-for"
                    placeholder="Order made for"
                    dataTestId="menu-item-detail-order-made-for"
                    component={TextInputField}
                    maxLength={ORDER_MADE_FOR_LENGTH}
                  />
                )}
                {showItemSpecialInstructionsField && (
                  <Field
                    name="specialInstructions"
                    placeholder="Special Instructions"
                    dataTestId="menu-item-detail-special-instructions"
                    component={MultiLineTextAreaField}
                    maxLength={SPECIAL_INSTRUCTIONS_LENGTH}
                  />
                )}
              </StyledBoxBorder>
            )}
          </Box>

          {isGroupOrder &&
          !isCurrentDinerAdmin &&
          (isCartClosed || isDinerDoneOrdering) ? (
            <GroupOrderOrderConfirmedSnackbar isCart={false} />
          ) : (
            <StickyPaper>
              <Box mt={2.5} mb={2.5} display="flex" alignItems="center">
                {!hasInvestorDayFeature && (
                  <Field
                    name="quantityInput"
                    component={QuantityInputField}
                    removeItemFromCart={this.removeItemFromCart}
                    disableDecrement={requestedQty === 1 || isUpdatingPreview}
                    disableIncrement={
                      isMaximumExceeded ||
                      previewedQuantityExceedsInventory ||
                      inventoryIsDepletedWithCurrentQuantity ||
                      isUpdatingPreview ||
                      hasExceededSpendingLimit
                    }
                  />
                )}

                {qtyCurrentlyInCart > 0 && qtyCurrentlyInCart === inventory && (
                  <Box ml={1}>
                    <MenuItemDescriptionTypography
                      variant={ITEM_DESCRIPTION_AND_PRICES}
                    >{`Last ${qtyCurrentlyInCart} in cart`}</MenuItemDescriptionTypography>
                  </Box>
                )}
                {/* don't display if inventoryLessCart - selected qty gets to zero, that indicates sold out */}
                {shouldDisplayInventoryLeft && (
                  <Box ml={1}>
                    <MenuItemDescriptionTypography
                      data-testid="menu-item-detail-inventory-left"
                      variant={ITEM_DESCRIPTION_AND_PRICES}
                    >{`Only ${lowInventoryMessageQuantity} left`}</MenuItemDescriptionTypography>
                  </Box>
                )}
                <MenuItemDetailPrice
                  cost={pricePreview}
                  hasInvestorDayFeature={hasInvestorDayFeature}
                />
              </Box>
              {isMaximumExceeded && (
                <Box mb={2.5}>
                  <Typography variant="caption">
                    {`You’ve reached the order maximum of $${orderMaximum}. Please
                  remove items from your cart or adjust their quantities to
                  continue to checkout.`}
                  </Typography>
                </Box>
              )}
              {shouldDisplayAddableInventory && (
                <Box mb={2.5}>
                  <MenuItemDescriptionTypography
                    data-testid="menu-item-detail-limited-quantity-caption"
                    variant="caption"
                  >
                    {`We have a limited quantity of ${productName} and only ${inventoryLessCart} can be added to your cart.`}
                  </MenuItemDescriptionTypography>
                </Box>
              )}
              {hasExceededSpendingLimit && (
                <Box mb={2.5}>
                  <MenuItemDescriptionTypography
                    data-testid="menu-item-detail-limited-quantity-caption"
                    variant="caption"
                  >
                    {`${SPENDING_LIMIT_REACHED.SPENDING_LIMIT_OF} $${spendingLimit}. ${SPENDING_LIMIT_REACHED.PLEASE_REMOVE_ITEMS}`}
                  </MenuItemDescriptionTypography>
                </Box>
              )}
              {/* the cartErrors object check is used to distinguish cart error from add to cart errors */}
              {hasCartError && typeof cartErrors[0] !== 'object' && (
                <Box mb={2.5}>
                  <Typography variant="caption">{cartErrors[0]}</Typography>
                </Box>
              )}
              <MenuItemSubmitContainer
                defaultCtaName={defaultCtaName}
                formType={formType}
                modifierGroups={modifierGroups}
                hasExceededSpendingLimit={hasExceededSpendingLimit}
              />
              <ItemDetailModalPreviewMenuMessage />
              {SkipToCheckoutButton}
            </StickyPaper>
          )}
        </StyledMenuDetailsModalBoxWrapper>
      </Form>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { restaurantLocations, cart, account } = state;
  const selectedLocation =
    restaurantLocations.byId[cart.online_ordering_location];

  const showItemSpecialInstructionsField = get(
    selectedLocation,
    'show_special_instructions'
  );

  const hasInvestorDayFeature = accountHasInvestorDayFeatureFlag(account);

  return {
    isAddingToCart: state.cart.isFetching,
    cartErrors: state.cart.error,
    itemForm: state.form,
    initialValues: state.editCart,
    account: state.account,
    showOrderMadeForField: selectedLocation?.add_made_for_field,
    showItemSpecialInstructionsField,
    ...getIsEnabledAddToCartSelector(state, ownProps.modifierGroups),
    isGroupOrder: selectCartIsGroupOrder(state),
    dinerAdminName: selectCartGroupOrderAdminName(state),
    isCartClosed: selectCartGroupOrderCartClosed(state),
    isDinerDoneOrdering: selectIsGroupOrderCurrentDinerDoneOrdering(state),
    isCurrentDinerAdmin: selectIsGroupOrderCurrentDinerAdmin(state),
    hasInvestorDayFeature
  };
};

/**
 * dispatching submit action when user submits redux form.
 * values, dispatch, props is coming from redux form.
 * see: https://redux-form.com/8.2.2/docs/api/reduxform.md/#-code-onsubmit-function-code-optional-
 */
let MenuItemDetailForm = reduxForm({
  form: 'menuItemDetail',
  onSubmit: (values, dispatch, props) =>
    dispatch(props.handleFormItemSubmit(values, props))
})(MenuItemDetailFormWrapper);

MenuItemDetailForm = withRouter(MenuItemDetailForm);

export default connect(mapStateToProps)(MenuItemDetailForm);
